import {
  AfterViewInit,
  Directive,
  Input,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { DomController, IonContent, Platform } from '@ionic/angular';
import { Platforms } from '@ionic/core';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Directive({
  selector: '[animationHeader]'
})
export class AnimationHeaderDirective implements AfterViewInit, OnDestroy {
  @Input() animationContent;
  @Input() animationDisableMode: Platforms;
  @Input() set animationToolbar(toolbar: any) {
    this.toolbar = toolbar && toolbar.el;
  }
  @Input() otherToolbars = [];

  private isExpanded = true;
  private toolbar: HTMLElement;
  private translateY = 0;
  private onContentDOMChanges = new Subject();
  private subscriptions: Subscription[] = [];

  private _toolbarContainer: HTMLElement;
  private _toolbarHeight;
  private _isScrollable = false;

  constructor(
    private renderer: Renderer2,
    private platform: Platform,
    private dom: DomController
  ) {}

  ngAfterViewInit() {
    if (!window) { return; }
    if (!this.toolbar || this.toolbar?.hidden) { return; }
    if (
      this.animationDisableMode &&
      this.platform.is(this.animationDisableMode)
    ) {
      return;
    }

    this.animationContent.forceOverscroll = false;

    setTimeout(() => {
      try {
        this.observeIonContentChanges();
        this._isScrollable = this.isScrollable;
        this._toolbarHeight = this.toolbarHeight;
        this._toolbarContainer = this.toolbarContainer;
        // Fix for ios to dont kill touch event when shrink/expand is applied
        this.renderer.setStyle(
          this.animationContent.el,
          'transform',
          `translateY(0px)`
        );
        if (!this.platform.is('ios')) {
          // Fix for ios to dont collide with bouncing
          this.renderer.setStyle(
            this.animationContent.el,
            'transition',
            'transform .3s ease-out'
          );
        }
      } catch {}
    }, 100);

    const hasScrolledDown$ = (this
      .animationContent as IonContent).ionScroll.pipe(
      filter(() => this.isScrollable),
      map(ev => ev.detail.scrollTop > 0)
    );

    const hasScrolledDownSubscription = hasScrolledDown$.subscribe(
      hasScrolledDown => {
        hasScrolledDown ? this.shrink() : this.expand();
      }
    );
    const resizeSubscription = fromEvent(window, 'resize').subscribe(() =>
      this.onResize()
    );
    const domChangesSubscription = this.onContentDOMChanges.subscribe(() =>
      this.onContentChanged()
    );

    this.subscriptions = [
      resizeSubscription,
      hasScrolledDownSubscription,
      domChangesSubscription
    ];
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private onResize() {
    this._isScrollable = null;
    this._toolbarHeight = null;
    this.renderer.setStyle(this.animationContent.el, 'min-height', null);
    this.animationContent.scrollToTop();
  }

  private onContentChanged() {
    this._isScrollable = null;
    this._toolbarHeight = null;
    if (!this.isScrollable) {
      this.expand();
    }
  }

  private get isScrollable() {
    if (!this._isScrollable) {
      try {
        this.renderer.setStyle(this.animationContent.el, 'min-height', null);
        const scrollArea = this.animationContent.el.querySelector(
          '.scroll-area'
        ).scrollHeight;
        const contentArea = this.animationContent.el.clientHeight;
        const smoothArea = 50;
        this._isScrollable = scrollArea - contentArea + smoothArea > this.toolbarHeight;
        if (this._isScrollable) {
          const height =
            contentArea + this.calculateTranslateY(this.toolbarHeight);
          this.renderer.setStyle(
            this.animationContent.el,
            'min-height',
            height + 'px'
          );
          this.translateY = this.calculateTranslateY(this.toolbarHeight);
        }
      } catch {}
    }

    return this._isScrollable;
  }

  private get toolbarHeight() {
    if (!this._toolbarHeight) {
      this._toolbarHeight = this.toolbar?.scrollHeight;
    }

    return this._toolbarHeight;
  }

  private get toolbarContainer() {
    if (!this._toolbarContainer) {
      try {
        this._toolbarContainer = this.toolbar?.shadowRoot.querySelector(
          '.toolbar-container'
        );
        this.renderer.setStyle(
          this._toolbarContainer,
          'transition',
          'transform .3s ease-out'
        );
        this.otherToolbars.forEach(({ el }) =>
          this.renderer.setStyle(el, 'transition', 'transform .3s ease-out')
        );
      } catch {}
    }

    return this._toolbarContainer;
  }

  private shrink() {
    if (!this.isExpanded) { return; }
    this.isExpanded = false;

    this.dom.write(() => {
      this.renderer.setStyle(
        this.toolbar,
        'transform',
        `translateY(-${this.translateY}px)`
      );
      this.renderer.setStyle(
        this.toolbarContainer,
        'transform',
        `translateY(${this.translateY / 2}px)`
      );
      this.renderer.setStyle(
        this.otherToolbars.length
          ? this.otherToolbars[this.otherToolbars.length - 1].el
          : this.toolbar,
        'box-shadow',
        '0 -9px 33px 0px rgba(0, 0, 0, 0.5)'
      );

      this.otherToolbars.forEach(({ el }) =>
        this.renderer.setStyle(
          el,
          'transform',
          `translateY(-${this.translateY}px)`
        )
      );

      this.renderer.setStyle(
        this.animationContent.el,
        'transform',
        `translateY(-${this.translateY - 1}px)`
      );
    });
  }

  private expand() {
    if (this.isExpanded) { return; }
    this.isExpanded = true;

    this.dom.write(() => {
      this.renderer.setStyle(this.toolbar, 'transform', `translateY(0px)`);
      this.renderer.setStyle(
        this.toolbarContainer,
        'transform',
        `translateY(0px)`
      );
      this.renderer.removeStyle(
        this.otherToolbars.length
          ? this.otherToolbars[this.otherToolbars.length - 1].el
          : this.toolbar,
        'box-shadow'
      );

      this.otherToolbars.forEach(({ el }) =>
        this.renderer.setStyle(el, 'transform', `translateY(0px)`)
      );

      this.renderer.setStyle(
        this.animationContent.el,
        'transform',
        `translateY(0px)`
      );
    });
  }

  private calculateTranslateY(height) {
    return Math.round(height * 0.3);
  }

  private observeIonContentChanges() {
    const node = this.animationContent.el;
    const observer = new MutationObserver(() => {
      this.onContentDOMChanges.next('mutation');
    });

    observer.observe(node, {
      childList: true,
      subtree: true
    });
  }
}
